import React from 'react';
import _ from 'lodash';

import { Input, Select } from '@miotech/mprod';

import { W } from '../i18n';
import './Pagination.global.styl';

const { Option } = Select;

interface Props {
  pageCount: number;
  currentPage: number;
  style: React.CSSProperties;
  onSelectPage: (page: number) => void;
  withSearch: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: ((pageSize: number) => void | Promise<void>) | null;
}

function Pagination({
  pageCount,
  currentPage,
  style,
  onSelectPage,
  withSearch,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}: Props) {
  const onPageRelative = (relative: number) => {
    let targetPage = currentPage + relative;
    if (targetPage > pageCount) {
      targetPage = pageCount;
    }
    if (targetPage < 1) {
      targetPage = 1;
    }
    onSelectPage(targetPage);
  };

  const onPage = (targetPage: number) => {
    if (currentPage !== targetPage) {
      onSelectPage(targetPage);
    }
  };

  /* Page number calculation */
  let displayPageCount = pageCount;
  if (displayPageCount < 1) {
    displayPageCount = 1;
  }

  if (displayPageCount <= 1 && !pageSizeOptions && !onPageSizeChange) {
    return null;
  }

  const pageNumbers = _.range(1, displayPageCount + 1);
  if (displayPageCount > 7) {
    if (currentPage >= 5) {
      if (currentPage < displayPageCount - 3) {
        pageNumbers.splice(1, currentPage - 3);
      } else {
        pageNumbers.splice(1, displayPageCount - 6);
      }
    }
    if (currentPage < displayPageCount - 3) {
      pageNumbers.splice(5, displayPageCount - pageNumbers[5]);
    }
  }

  const prevArrow = (
    <button
      className="mio-pagination__item"
      onClick={() => onPageRelative(-1)}
      disabled={currentPage <= 1}
    >
      <i className="iconfont icon-arrow" />
    </button>
  );
  const nextArrow = (
    <button
      className="mio-pagination__item"
      onClick={() => onPageRelative(1)}
      disabled={currentPage >= displayPageCount}
    >
      <i className="iconfont icon-arrow_right" />
    </button>
  );

  const pageSizeSelect =
    pageSizeOptions && onPageSizeChange && pageSize ? (
      <Select
        style={{ marginRight: 20, width: 50 }}
        size="small"
        value={`${pageSize}`}
        onChange={v => {
          onPageSizeChange(v ? Number(v) : 10);
        }}
      >
        {pageSizeOptions
          .map(n => `${n}`)
          .map(i => (
            <Option value={i} key={i}>
              {i}
            </Option>
          ))}
      </Select>
    ) : null;

  return withSearch ? (
    <div className="mio-pagination__with-search" style={style}>
      {pageSizeSelect}
      <span>{W('shared.pagination.page')}</span>
      <Input.NumberInput
        key={currentPage}
        defaultValue={currentPage}
        onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
          onPage(Math.min(displayPageCount, Math.max(1, Number(event.target.value))))
        }
        onPressEnter={(event: React.ChangeEvent<HTMLInputElement>) =>
          onPage(Math.min(displayPageCount, Math.max(1, Number(event.target.value))))
        }
        // @ts-ignore TODO: fix mprod
        min={1}
        max={displayPageCount}
      />
      <span style={{ whiteSpace: 'pre' }}>
        {W('shared.pagination.total', { count: displayPageCount })}
      </span>
      {prevArrow}
      {nextArrow}
    </div>
  ) : (
    <div className="mio-pagination" style={style}>
      {pageSizeSelect}
      {prevArrow}

      {pageNumbers.map((pageNumber, index) => {
        if (pageNumber !== undefined) {
          return (
            <div
              key={pageNumber}
              className={`mio-pagination__item ${
                currentPage === pageNumber
                  ? 'mio-pagination__item--selected'
                  : 'mio-pagination__item--hoverable'
              }`}
              onClick={() => onPage(pageNumber)}
            >
              {pageNumber}
            </div>
          );
        }
        return (
          <div key={`more-${index}`} className="mio-pagination__item">
            ...
          </div>
        );
      })}

      {nextArrow}
    </div>
  );
}

Pagination.defaultProps = {
  pageCount: 1,
  currentPage: 1,
  style: {},
  onSelectPage: () => {},
  withSearch: false,
} as Partial<Props>;

export default Pagination;
